import { z } from 'zod/v4';

/**
 * Path parameters of `POST /api/questionnaires/requests/:requestId/responses`.
 *
 * The legacy route was a flat `POST /v1/responses` that carried `requestId` on
 * **every item** of the body's `payload` array, which in principle let one call
 * write answers across several answer requests. Nothing ever did: of the six call
 * sites, four send a single item, and the two bulk auto-fill flows send N items
 * that all carry the same id — `frontend-v2` hardcodes `request.id` for every
 * item, and the old frontend's `InitialModal` reads `question.questionnaireRequestId`,
 * which the answer page sets from its single `useParams()` route id on every
 * question it loads.
 *
 * Hoisting it into the path makes the request the addressed resource, matching the
 * sibling comment and status endpoints, and means the tenancy gate runs once per
 * call instead of being re-verified per item.
 *
 * The 64-character cap is the `request_id` column's width.
 */
export const CreateQuestionResponseParamsSchema = z.object({
  requestId: z.string().min(1).max(64),
});

export type CreateQuestionResponseParams = z.infer<
  typeof CreateQuestionResponseParamsSchema
>;

/**
 * The four statuses an individual answer can hold — the legacy
 * `RESPONSE_STATUS`, which was `Object.keys(RESPONSE_STATUS_ENUM)`.
 *
 * `NOT_SUBMITTED` is a draft the supplier is still editing, `IN_REVIEW` is
 * awaiting a reviewer, and `APPROVED` / `DENIED` are the reviewer's verdict.
 */
export const QUESTION_RESPONSE_STATUSES = [
  'APPROVED',
  'DENIED',
  'IN_REVIEW',
  'NOT_SUBMITTED',
] as const;

export type QuestionResponseStatus =
  (typeof QUESTION_RESPONSE_STATUSES)[number];

/**
 * What an answer value may be.
 *
 * The legacy `anyOf` verbatim: string, number, object, array or null. **Boolean is
 * deliberately absent** — it was absent there too, so a bare `true` has always
 * been a 400 on this endpoint, including for `BOOLEAN`-typed questions (both
 * frontends wrap every answer in a `{ value }` object). Adding it would widen the
 * contract past what the storage path has ever been exercised with.
 *
 * The array branch is listed before the record branch so a JSON array is never
 * matched as an object.
 *
 * Whatever arrives here is `JSON.stringify`'d before it reaches the `answer`
 * column — see `upsertQuestionResponseDb`.
 */
const QuestionResponseAnswerSchema = z.union([
  z.string(),
  z.number(),
  z.array(z.unknown()),
  z.record(z.string(), z.unknown()),
  z.null(),
]);

/**
 * One answer in the batch.
 *
 * `answer` is a **required key that accepts `null`** — clearing an answer is a
 * normal write, and the legacy schema required the key while allowing the null
 * type. `questionId`, `answer` and `isNotAvailable` were all in the legacy
 * `required` list and stay required; `requestId` was too, and is now the path
 * param.
 *
 * Three deviations from the legacy item schema, each deliberate:
 *
 * - **`status` is declared here even though the legacy JSON Schema omitted it.**
 *   It survived legacy validation only because the body was not
 *   `additionalProperties: false`, and the service genuinely consumed it —
 *   `if (!status) status = 'IN_REVIEW'`. Both frontends send
 *   `status: 'NOT_SUBMITTED'` on every autosave. Leaving it out of the contract
 *   would let zod strip it and quietly file every draft as `IN_REVIEW`, so a
 *   half-typed answer would show up on the reviewer's queue. Constraining it to
 *   the four known members is a tightening: legacy accepted any string and wrote
 *   it straight to the column.
 * - **`files` is dropped.** The legacy schema declared it (with a misplaced
 *   `required: ['fileId']` that could never apply to an array), but nothing in the
 *   create chain ever read it — the file path derives its file list from
 *   `answer.value`, not from this field.
 * - **`isNotAvailable` gains `.int()`.** Legacy said `type: number, minimum: 0,
 *   maximum: 1`, so `0.5` passed validation and then failed against the integer
 *   column as a 500. It is a 400 now.
 */
export const CreateQuestionResponseItemSchema = z.object({
  questionId: z.string().min(1).max(64),
  answer: QuestionResponseAnswerSchema,
  isNotAvailable: z.number().int().min(0).max(1),
  status: z.enum(QUESTION_RESPONSE_STATUSES).optional(),
  autoAnswered: z.boolean().optional(),
});

export type CreateQuestionResponseItem = z.infer<
  typeof CreateQuestionResponseItemSchema
>;

/**
 * Provenance of an auto-filled answer, stamped on every row in the batch.
 *
 * `looseObject` because the legacy schema was `additionalProperties: true` and the
 * SLCP prefill relies on it: it sends `objectId` and `issueDate` (not the
 * documented `issuedOn`) alongside the declared keys, and the whole object is
 * serialised into `verification_meta` as-is. A strict object would silently drop
 * exactly the fields that flow identifies an SLCP assessment by.
 *
 * Its presence — not its contents — is what sets `is_verified` on the row.
 *
 * **Top-level only, by design.** The old frontend's bulk auto-fill also puts a
 * `verificationMeta` on each payload item; the legacy service overwrote it with
 * the top-level value (or `null`) before every insert, so those per-item values
 * have never been stored. Accepting them per item now would start persisting data
 * that has never existed on this table.
 */
export const QuestionResponseVerificationMetaSchema = z.looseObject({
  id: z.string().optional(),
  name: z.string().optional(),
  issuedOn: z.string().optional(),
  version: z.string().optional(),
});

export type QuestionResponseVerificationMeta = z.infer<
  typeof QuestionResponseVerificationMetaSchema
>;

/**
 * Body of `POST /api/questionnaires/requests/:requestId/responses`.
 *
 * `payload` is **required** here. The legacy body schema listed no `required` at
 * all, so a request without it reached the handler, which wrapped `undefined` in
 * an array and then destructured it — a 500. It is a 400 now.
 *
 * An empty `payload` is valid and writes nothing, answering 201. The legacy
 * integration test's unauthenticated case relied on that shape.
 */
export const CreateQuestionResponseBodySchema = z.object({
  verificationMeta:
    QuestionResponseVerificationMetaSchema.nullable().optional(),
  payload: z.array(CreateQuestionResponseItemSchema),
});

export type CreateQuestionResponseBody = z.infer<
  typeof CreateQuestionResponseBodySchema
>;
